<template>
  <v-app class="bg-white">
    <v-main class="auth-pages">
      <v-container fluid>
        <v-row class="fill-height">
          <v-col cols="12" md="6" class="pa-0 d-none d-md-block">
            <div class="position-relative h-100">
              <div class="position-absolute w-100 h-100 bg-dark-blue">
                <div class="d-flex flex-column justify-center align-center h-100 text-center">
                  <img src="@/assets/img/logo.png" alt="Logo" class="mb-8" width="200">
                  <h2 class="text-h3 font-weight-bold text-white mb-4">
                    AutoSyncX™️ Rental System
                  </h2>
                  <p class="text-subtitle-1 text-white px-10">
                    Login to access your rental bookings, manage your account, and book new vehicles.
                  </p>
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6" class="pa-10">
            <div class="d-flex justify-center align-center h-100">
              <v-card class="elevation-0 px-0" width="100%" max-width="500">
                <v-card-title class="text-h4 font-weight-bold text-center mb-6">
                  {{ isLogin ? 'Login' : 'Sign Up' }}
                </v-card-title>
                
                <v-alert v-if="errorMessage" type="error" class="mb-4">
                  {{ errorMessage }}
                </v-alert>
                
                <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="isLogin ? login() : register()">
                  <!-- Login Form -->
                  <div v-if="isLogin">
                    <v-text-field
                      v-model="loginForm.email"
                      label="Email"
                      type="email"
                      :rules="emailRules"
                      required
                      outlined
                      dense
                      class="mb-4"
                    ></v-text-field>
                    
                    <v-text-field
                      v-model="loginForm.password"
                      label="Password"
                      :type="showPassword ? 'text' : 'password'"
                      :rules="passwordRules"
                      required
                      outlined
                      dense
                      class="mb-4"
                      :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      @click:append="showPassword = !showPassword"
                    ></v-text-field>
                    
                    <div class="d-flex justify-space-between align-center mb-6">
                      <v-checkbox
                        v-model="rememberMe"
                        label="Remember me"
                        color="primary"
                        hide-details
                        class="mt-0"
                      ></v-checkbox>
                      
                      <a href="#" class="text-decoration-none" @click.prevent="forgotPassword = true">
                        Forgot password?
                      </a>
                    </div>
                    
                    <v-btn
                      color="primary"
                      block
                      large
                      type="submit"
                      :loading="loading"
                      :disabled="!valid || loading"
                    >
                      Login
                    </v-btn>
                    
                    <div class="text-center mt-6">
                      <span>Don't have an account?</span>
                      <a href="#" class="text-decoration-none ml-2" @click.prevent="isLogin = false">
                        Sign up
                      </a>
                    </div>
                  </div>
                  
                  <!-- Register Form -->
                  <div v-else>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="registerForm.first_name"
                          label="First Name"
                          :rules="nameRules"
                          required
                          outlined
                          dense
                          class="mb-4"
                        ></v-text-field>
                      </v-col>
                      
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="registerForm.last_name"
                          label="Last Name"
                          :rules="nameRules"
                          required
                          outlined
                          dense
                          class="mb-4"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    
                    <v-text-field
                      v-model="registerForm.email"
                      label="Email"
                      type="email"
                      :rules="emailRules"
                      required
                      outlined
                      dense
                      class="mb-4"
                    ></v-text-field>
                    
                    <v-text-field
                      v-model="registerForm.phone"
                      label="Phone"
                      :rules="phoneRules"
                      required
                      outlined
                      dense
                      class="mb-4"
                      v-mask="'(###) ###-####'"
                    ></v-text-field>
                    
                    <v-btn
                      color="primary"
                      block
                      large
                      type="submit"
                      :loading="loading"
                      :disabled="!valid || loading"
                    >
                      Sign Up
                    </v-btn>
                    
                    <div class="text-center mt-6">
                      <span>Already have an account?</span>
                      <a href="#" class="text-decoration-none ml-2" @click.prevent="isLogin = true">
                        Login
                      </a>
                    </div>
                  </div>
                </v-form>
              </v-card>
            </div>
          </v-col>
        </v-row>
      </v-container>
      
      <!-- Forgot Password Dialog -->
      <v-dialog v-model="forgotPassword" max-width="500px">
        <v-card>
          <v-card-title class="text-h5">Reset Password</v-card-title>
          
          <v-card-text>
            <p class="mb-4">Enter your email address and we'll send you a link to reset your password.</p>
            
            <v-text-field
              v-model="forgotPasswordEmail"
              label="Email"
              type="email"
              :rules="emailRules"
              required
              outlined
              dense
            ></v-text-field>
          </v-card-text>
          
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="forgotPassword = false">Cancel</v-btn>
            <v-btn color="primary" @click="resetPassword" :loading="resetLoading">
              Reset Password
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      
      <!-- Success Dialog -->
      <v-dialog v-model="successDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h5">{{ successTitle }}</v-card-title>
          
          <v-card-text>
            <p>{{ successMessage }}</p>
          </v-card-text>
          
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="successDialog = false">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
  </v-app>
</template>

<script>
export default {
  name: 'RentalLogin',
  
  data() {
    return {
      isLogin: true,
      valid: false,
      loading: false,
      resetLoading: false,
      showPassword: false,
      rememberMe: false,
      errorMessage: null,
      forgotPassword: false,
      forgotPasswordEmail: '',
      successDialog: false,
      successTitle: '',
      successMessage: '',
      
      loginForm: {
        email: '',
        password: ''
      },
      
      registerForm: {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      },
      
      nameRules: [
        v => !!v || 'Name is required'
      ],
      
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ],
      
      passwordRules: [
        v => !!v || 'Password is required',
        v => v.length >= 6 || 'Password must be at least 6 characters'
      ],
      
      phoneRules: [
        v => !!v || 'Phone number is required',
        v => v.replace(/\D/g, '').length === 10 || 'Phone number must be valid'
      ]
    }
  },
  
  methods: {
    login() {
      if (this.$refs.form.validate()) {
        this.loading = true
        this.errorMessage = null
        
        axios.post('/api/rental/login', {
          email: this.loginForm.email,
          password: this.loginForm.password
        })
        .then(response => {
          if (response.data.flag === 1) {
            // Save token and user data
            localStorage.setItem('rental_token', response.data.token.token)
            localStorage.setItem('rental_user', JSON.stringify(response.data.data))
            
            // Redirect to dashboard
            this.$router.push('/rental/dashboard')
          } else {
            this.errorMessage = response.data.message || 'Login failed. Please check your credentials.'
          }
        })
        .catch(error => {
          console.error('Login error:', error)
          this.errorMessage = error.response?.data?.message || 'An error occurred during login. Please try again.'
        })
        .finally(() => {
          this.loading = false
        })
      }
    },
    
    register() {
      if (this.$refs.form.validate()) {
        this.loading = true
        this.errorMessage = null
        
        axios.post('/api/rental/register', {
          first_name: this.registerForm.first_name,
          last_name: this.registerForm.last_name,
          email: this.registerForm.email,
          phone: this.registerForm.phone.replace(/\D/g, '')
        })
        .then(response => {
          if (response.data.flag === 1) {
            // Check if user already exists
            if (response.data.message === 'User already exists') {
              // Show success dialog
              this.successTitle = 'Account Already Exists'
              this.successMessage = 'An account with this email already exists. Please login with your credentials.'
              this.successDialog = true
              this.isLogin = true
            } else {
              // Save token and user data for new registration
              localStorage.setItem('rental_token', response.data.token.token)
              localStorage.setItem('rental_user', JSON.stringify(response.data.data))
              
              // Show success dialog
              this.successTitle = 'Registration Successful'
              this.successMessage = 'Your account has been created successfully. A temporary password has been sent to your email.'
              this.successDialog = true
              
              // Redirect to dashboard after closing dialog
              this.$watch('successDialog', (val) => {
                if (!val) {
                  this.$router.push('/rental/dashboard')
                }
              })
            }
          } else {
            this.errorMessage = response.data.message || 'Registration failed. Please try again.'
          }
        })
        .catch(error => {
          console.error('Registration error:', error)
          this.errorMessage = error.response?.data?.message || 'An error occurred during registration. Please try again.'
        })
        .finally(() => {
          this.loading = false
        })
      }
    },
    
    resetPassword() {
      if (!this.forgotPasswordEmail || !/.+@.+\..+/.test(this.forgotPasswordEmail)) {
        this.errorMessage = 'Please enter a valid email address'
        return
      }
      
      this.resetLoading = true
      this.errorMessage = null
      
      axios.post('/api/rental/reset-password', {
        email: this.forgotPasswordEmail
      })
      .then(response => {
        if (response.data.flag === 1) {
          this.forgotPassword = false
          this.successTitle = 'Password Reset Email Sent'
          this.successMessage = 'Check your email for instructions to reset your password.'
          this.successDialog = true
          this.forgotPasswordEmail = ''
        } else {
          this.errorMessage = response.data.message || 'Password reset failed. Please try again.'
        }
      })
      .catch(error => {
        console.error('Password reset error:', error)
        this.errorMessage = error.response?.data?.message || 'An error occurred. Please try again.'
      })
      .finally(() => {
        this.resetLoading = false
      })
    }
  }
}
</script>

<style scoped>
.auth-pages {
  background-color: #f5f5f5;
  min-height: 100vh;
}

.bg-dark-blue {
  background-color: #1a237e;
}

.fill-height {
  height: 100vh;
}
</style>
